import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProfileMenu from "../components/ProfileMenu";
import NotificationBell from "../components/NotificationBell";
import NotificationCenter from "../components/NotificationCenter";
import ManagerAlerts from "../components/ManagerAlerts";
import KpiColumn from "../components/KpiColumn";
import PortalSearch from "../components/PortalSearch";
import { AccessLockedModal } from "../components/AccessLocked";
import { hasDomainAccess, firstAccessiblePath } from "../permissionConfig";

/**
 * Soulflower Internal Portal — Landing page
 * ------------------------------------------------
 * Visual design matches the approved InternalPortal.jsx / PDF mockup.
 * Fluid/responsive: no fixed 1920x1080 canvas — the grid reflows and
 * stacks on smaller screens, and always fills the available viewport
 * without a fixed-size overflow.
 */

// --- department data (drives both the routing and the tiles) -----------
const DEPARTMENTS = [
  {
    id: "sales",
    label: "Sales",
    desc: "Daily MIS, SKU performance, city analytics and platform reports.",
    tint: "#EEE9F8",
    color: "#6E58A6",
    active: true,
    path: "/sales",
    icon: (
      <>
        <path d="M3 3v18h18" />
        <path d="M7 14l3-4 3 2 5-6" />
      </>
    ),
    watermark: (
      <>
        <path d="M3 3v18h18" />
        <path d="M7 15l4-5 3 3 5-7" />
      </>
    ),
  },
  {
    id: "inventory",
    label: "Inventory",
    desc: "Inward & outward stock management with a live current-stock dashboard.",
    tint: "#E7F0E8",
    color: "#5C7E60",
    active: true,
    path: "/inventory",
    icon: (
      <>
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <path d="M3.27 6.96L12 12l8.73-5.04M12 22V12" />
      </>
    ),
    watermark: (
      <>
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <path d="M3.27 6.96L12 12l8.73-5.04M12 22V12" />
      </>
    ),
  },
  {
    id: "dms",
    label: "DMS",
    desc: "Distributor management — sales analytics and inventory projection.",
    tint: "#F5EDDC",
    color: "#9A7B3E",
    active: true,
    path: "/dms",
    icon: (
      <>
        <path d="M10 17h4V5H2v12h3" />
        <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
        <circle cx="7.5" cy="17.5" r="2.5" />
        <circle cx="17.5" cy="17.5" r="2.5" />
      </>
    ),
    watermark: (
      <>
        <path d="M10 17h4V5H2v12h3" />
        <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
        <circle cx="7.5" cy="17.5" r="2.5" />
        <circle cx="17.5" cy="17.5" r="2.5" />
      </>
    ),
  },
  {
    id: "platform",
    label: "Platform",
    desc: "SKU availability tracking across quick-commerce platforms by city & pincode.",
    tint: "#F7E5EA",
    color: "#B05770",
    active: true,
    path: "/platform/availability",
    icon: (
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </>
    ),
    watermark: (
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M9 6h6M9 10h6M9 14h4" />
      </>
    ),
  },
  {
    id: "po_so_lifecycle",
    label: "PO & SO Lifecycle",
    desc: "Track platform purchase orders through SO dispatch and KAM validation.",
    tint: "#E5EEF7",
    color: "#4A6FA5",
    active: true,
    path: "/po-so-lifecycle",
    icon: (
      <>
        <path d="M17 2l4 4-4 4" />
        <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
        <path d="M7 22l-4-4 4-4" />
        <path d="M21 13v1a4 4 0 0 1-4 4H3" />
      </>
    ),
    watermark: (
      <>
        <path d="M17 2l4 4-4 4" />
        <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
        <path d="M7 22l-4-4 4-4" />
        <path d="M21 13v1a4 4 0 0 1-4 4H3" />
      </>
    ),
  },
  {
    id: "invoicing",
    label: "Invoicing",
    desc: "Invoice generation from dispatch data, advance booking and invoice queue.",
    tint: "#E3F2F0",
    color: "#2F8F82",
    active: true,
    path: "/invoicing",
    icon: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M16 13H8M16 17H8M10 9H8" />
      </>
    ),
    watermark: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M16 13H8M16 17H8M10 9H8" />
      </>
    ),
  },
  {
    id: "d2c",
    label: "D2C",
    desc: "Shopify order fulfillment tracking — fill rate, trends and unfulfilled orders.",
    tint: "#F5E4DC",
    color: "#B0553F",
    active: true,
    path: "/d2c",
    icon: (
      <>
        <path d="M6 2 3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-3-5Z" />
        <path d="M3 7h18" />
        <path d="M16 11a4 4 0 0 1-8 0" />
      </>
    ),
    watermark: (
      <>
        <path d="M6 2 3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-3-5Z" />
        <path d="M3 7h18" />
        <path d="M16 11a4 4 0 0 1-8 0" />
      </>
    ),
  },
  {
    id: "pod_upload",
    label: "POD Upload",
    desc: "Upload proof-of-delivery photos and browse/export past hand-delivery PODs by date.",
    tint: "#F1EEF6",
    color: "#3A3A3A",
    active: true,
    path: "/pod-upload",
    icon: (
      <>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
        <circle cx="12" cy="13" r="4" />
      </>
    ),
    watermark: (
      <>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
        <circle cx="12" cy="13" r="4" />
      </>
    ),
  },
  { id: "finance", label: "Finance & Accounts", active: false },
  { id: "hr", label: "HR & Payroll", active: false },
  { id: "scm", label: "Procurement", active: false },
];

// --- small svg helper ----------------------------------------------------
const svg = (size, children, extra = {}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...extra}
  >
    {children}
  </svg>
);

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

function getDate() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// --- component -------------------------------------------------------
export default function Landing({ user, onLogout = () => {} }) {
  const navigate = useNavigate();
  const [lockedDept, setLockedDept] = useState(null);

  // Caps "Recent Activity"'s height to match the left column's natural
  // content height (tiles + "Coming Soon" row) so it scrolls internally
  // instead of stretching the whole grid row (and the whole page) to match
  // an ever-growing notification list — CSS Grid's align-items:stretch has
  // no way to cap one column to a sibling's independently-rendered height,
  // so this is measured via ResizeObserver instead. Only applied in the
  // 3-column desktop layout (matches .sfip-grid's own 1180px breakpoint
  // below) — once the columns stack on a narrower screen, "aligning with
  // Coming Soon" no longer means anything, so the cap is released there.
  const leftColRef = React.useRef(null);
  const [leftColHeight, setLeftColHeight] = useState(null);

  useEffect(() => {
    const el = leftColRef.current;
    if (!el) return undefined;

    function update() {
      if (window.innerWidth > 1180) {
        setLeftColHeight(el.getBoundingClientRect().height);
      } else {
        setLeftColHeight(null);
      }
    }

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const displayName = user?.display_name || user?.username || "there";
  const role = user?.role || "";
  const isAdmin = role?.toLowerCase() === "admin";

  const activeDepts = DEPARTMENTS.filter((d) => d.active);
  const soonDepts = DEPARTMENTS.filter((d) => !d.active);

  function handleDept(dept) {
    if (!dept.active || !dept.path) return;
    const target = firstAccessiblePath(user, dept.id);
    if (target) navigate(target);
    else setLockedDept(dept.label);
  }

  function handleDeptKeyDown(e, dept) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleDept(dept);
    }
  }

  return (
    <div className="sfip-page">
      <style>{CSS}</style>

      {/* NAV */}
      <nav className="sfip-nav">
        <div className="sfip-nav-left">
          <img
            src="/soulflower-logo.png"
            alt="Soulflower"
            className="sfip-logo"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <span className="sfip-logo-fallback">SOULFLOWER</span>
          <span className="sfip-divider" />
          <span className="sfip-portal-label">Internal Portal</span>
        </div>

        <div className="sfip-nav-center">
          <PortalSearch user={user} />
        </div>

        <div className="sfip-nav-right">
          <NotificationCenter user={user} className="sfip-bell" />

          {isAdmin && (
            <button className="sfip-admin-btn" onClick={() => navigate("/admin")}>
              {svg(18, (
                <>
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </>
              ))}
              Admin Centre
            </button>
          )}

          <span className="sfip-divider" />

          <NotificationBell user={user} compact />

          <ProfileMenu user={user} onLogout={onLogout} />
        </div>
      </nav>

      <div className="sfip-body">
        {/* HEADER */}
        <header className="sfip-header">
          <div>
            <div className="sfip-greeting">
              {getGreeting().toUpperCase()}, {displayName.toUpperCase()}
            </div>
            <h1 className="sfip-h1">
              Business <span className="sfip-h1-accent">Intelligence</span>
            </h1>
          </div>
          <div className="sfip-date">{getDate()}</div>
        </header>

        {/* GRID */}
        <div className="sfip-grid">
          {/* WORKSPACES */}
          <div className="sfip-col" ref={leftColRef}>
            <div className="sfip-section-label">Department Workspaces</div>
            <div className="sfip-tiles">
              {activeDepts.map((dept) => {
                const locked = !hasDomainAccess(user, dept.id);
                return (
                  <div
                    key={dept.id}
                    className="sfip-tile"
                    data-tutorial={`landing-tile-${dept.id}`}
                    style={{ "--accent": dept.color, "--tint": dept.tint }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDept(dept)}
                    onKeyDown={(e) => handleDeptKeyDown(e, dept)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={dept.color}
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="sfip-watermark"
                    >
                      {dept.watermark}
                    </svg>
                    <div className="sfip-tile-inner">
                      <div className="sfip-tile-icon">{svg(22, dept.icon)}</div>
                      {locked && (
                        <span className="sfip-tile-lock">
                          {svg(11, (
                            <>
                              <rect x="3" y="11" width="18" height="11" rx="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </>
                          ), { strokeWidth: 2.3 })}
                        </span>
                      )}
                      <h3 className="sfip-tile-title">{dept.label}</h3>
                      <p className="sfip-tile-desc">{dept.desc}</p>
                      <span className="sfip-tile-link">
                        {locked ? "No access" : `Open ${dept.label} →`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sfip-soon-row">
              <span className="sfip-section-label sfip-soon-label">Coming Soon</span>
              <div className="sfip-soon-chips">
                {soonDepts.map((d) => (
                  <span key={d.id} className="sfip-soon-chip">
                    {d.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* KPI — real data, permission-filtered (falls back to reserved placeholder) */}
          <KpiColumn user={user} />

          {/* ALERTS — real data, permission-filtered */}
          <ManagerAlerts user={user} maxHeight={leftColHeight} />
        </div>
      </div>

      <AccessLockedModal open={!!lockedDept} label={lockedDept} onClose={() => setLockedDept(null)} />
    </div>
  );
}

// --- styles (real CSS, so we can use media queries for fluid layout) ---
const CSS = `
  :root {
    --sfip-ink: #231C30;
    --sfip-muted: #8C82A0;
    --sfip-line: #ECE7F2;
  }

  .sfip-page {
    min-height: 100vh;
    width: 100%;
    background: #FAF8FC;
    color: var(--sfip-ink);
    font-family: 'Poppins', 'Segoe UI', sans-serif;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .sfip-page *, .sfip-page *::before, .sfip-page *::after { box-sizing: border-box; }

  /* NAV */
  .sfip-nav {
    position: relative;
    z-index: 20;
    flex: none;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    padding: 16px clamp(20px, 4vw, 60px);
    border-bottom: 1px solid var(--sfip-line);
    background: #fff;
  }
  .sfip-nav-left { display: flex; align-items: center; gap: 16px; min-width: 0; }
  .sfip-nav-center {
    width: 100%;
    min-width: 0;
    max-width: 960px;
    margin: 0 auto;
  }
  @media (max-width: 480px) {
    .sfip-nav { grid-template-columns: 1fr auto; }
    .sfip-nav-center { grid-column: 1 / -1; order: 3; max-width: none; }
  }
  .sfip-logo { height: 34px; width: auto; display: block; }
  .sfip-logo-fallback {
    display: none;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 20px;
    letter-spacing: 0.14em;
    font-weight: 600;
    color: var(--sfip-ink);
  }
  .sfip-divider { width: 1px; height: 30px; background: #DDD5E8; flex: none; }
  .sfip-portal-label {
    font-size: 12px; letter-spacing: 0.22em; font-weight: 600;
    color: var(--sfip-muted); text-transform: uppercase; white-space: nowrap;
  }
  .sfip-nav-right { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

  .sfip-bell {
    position: relative; width: 40px; height: 40px; border-radius: 11px;
    border: 1px solid #E4DEEE; background: #fff; color: #6B5E86;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
  }

  .sfip-admin-btn {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 18px; border-radius: 11px; border: none;
    background: #6E58A6; color: #fff; font-family: inherit;
    font-weight: 500; font-size: 13.5px; cursor: pointer;
    transition: background 0.15s;
  }
  .sfip-admin-btn:hover { background: #5D4A8C; }

  .sfip-user {
    display: flex; align-items: center; gap: 12px;
    background: transparent; border: none; cursor: pointer;
    padding: 4px 8px; border-radius: 12px; font-family: inherit;
    transition: background 0.15s;
  }
  .sfip-user:hover { background: #F5F2FA; }
  .sfip-avatar {
    width: 42px; height: 42px; border-radius: 12px; flex: none;
    background: linear-gradient(135deg, #8E74C9, #5D4A8C);
    display: flex; align-items: center; justify-content: center; color: #fff;
  }
  .sfip-user-text { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.2; }
  .sfip-user-name { font-weight: 600; font-size: 15px; color: var(--sfip-ink); }
  .sfip-user-role { font-size: 12px; color: var(--sfip-muted); text-transform: capitalize; }

  /* BODY */
  .sfip-body {
    flex: 1; min-height: 0;
    padding: clamp(20px, 3vw, 34px) clamp(20px, 4vw, 60px) clamp(20px, 3vw, 38px);
    display: flex; flex-direction: column;
  }

  .sfip-header {
    flex: none; display: flex; justify-content: space-between; align-items: flex-end;
    flex-wrap: wrap; gap: 12px; margin-bottom: 24px;
  }
  .sfip-greeting { font-size: 11.5px; letter-spacing: 0.22em; font-weight: 600; color: #8C6FC0; margin-bottom: 10px; }
  .sfip-h1 { font-weight: 300; font-size: clamp(28px, 4vw, 44px); line-height: 1; margin: 0; letter-spacing: -0.02em; }
  .sfip-h1-accent { font-weight: 600; color: #6E58A6; }
  .sfip-date { font-size: 15px; color: var(--sfip-muted); font-weight: 500; }

  /* GRID */
  .sfip-grid {
    flex: 1; min-height: 0;
    display: grid;
    grid-template-columns: minmax(320px, 1.4fr) minmax(260px, 1fr) minmax(260px, 1fr);
    gap: clamp(14px, 2vw, 22px);
  }
  @media (max-width: 1180px) {
    .sfip-grid { grid-template-columns: 1fr 1fr; }
    .sfip-col { grid-column: 1 / -1; }
  }
  @media (max-width: 720px) {
    .sfip-grid { grid-template-columns: 1fr; }
    .sfip-col { grid-column: auto; }
  }

  .sfip-col { min-height: 0; display: flex; flex-direction: column; }
  .sfip-section-label {
    flex: none; font-size: 11.5px; letter-spacing: 0.2em; font-weight: 600;
    color: var(--sfip-muted); text-transform: uppercase; margin-bottom: 14px;
  }

  .sfip-tiles {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 14px;
  }

  .sfip-tile {
    position: relative; overflow: hidden;
    padding: 20px 22px; border-radius: 15px; background: #fff;
    border: 1px solid var(--sfip-line);
    display: flex; flex-direction: column; cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  }
  .sfip-tile:hover, .sfip-tile:focus-visible {
    transform: translateY(-3px);
    box-shadow: 0 10px 28px rgba(110, 88, 166, 0.16);
    border-color: var(--accent);
  }
  .sfip-tile:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .sfip-watermark { position: absolute; right: -18px; bottom: -20px; width: 120px; height: 120px; opacity: 0.06; pointer-events: none; }
  .sfip-tile-inner { position: relative; display: flex; flex-direction: column; height: 100%; }
  .sfip-tile-icon {
    width: 42px; height: 42px; border-radius: 12px; margin-bottom: 12px;
    display: flex; align-items: center; justify-content: center;
    background: var(--tint); color: var(--accent);
  }
  .sfip-tile-lock {
    position: absolute; top: 20px; right: 22px; width: 26px; height: 26px; border-radius: 8px;
    background: #F1EEF6; color: #B7AFC6; display: flex; align-items: center; justify-content: center;
  }
  .sfip-tile-title { font-size: 20px; font-weight: 500; margin: 0 0 6px; color: var(--sfip-ink); }
  .sfip-tile-desc { margin: 0; font-size: 13px; line-height: 1.5; color: #6B6379; font-weight: 300; flex: 1; }
  .sfip-tile-link { margin-top: 14px; font-weight: 500; font-size: 13.5px; color: var(--accent); }

  .sfip-soon-row { flex: none; display: flex; align-items: center; gap: 14px; margin-top: 18px; flex-wrap: wrap; }
  .sfip-soon-label { margin: 0; white-space: nowrap; }
  .sfip-soon-chips { display: flex; flex-wrap: wrap; gap: 10px; }
  .sfip-soon-chip {
    padding: 9px 15px; border-radius: 11px; border: 1px dashed #D6CCE8;
    background: #fff; font-size: 12.5px; font-weight: 400; color: #9A90AE;
  }

  .sfip-kpi {
    min-height: 200px; border-radius: 16px; border: 1.5px dashed #D6CCE8;
    background: repeating-linear-gradient(135deg, #FCFBFE, #FCFBFE 14px, #F7F4FB 14px, #F7F4FB 28px);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 32px;
  }
  .sfip-kpi-icon {
    width: 56px; height: 56px; border-radius: 16px; background: #EEE9F8;
    display: flex; align-items: center; justify-content: center; color: #8C6FC0; margin-bottom: 18px;
  }
  .sfip-kpi-title { font-size: 19px; font-weight: 500; color: var(--sfip-ink); }
  .sfip-kpi-text { font-size: 13px; color: #9A90AE; font-weight: 300; margin-top: 8px; max-width: 240px; line-height: 1.5; }

  .sfip-alerts {
    min-height: 200px; border-radius: 16px; background: #fff; border: 1px solid var(--sfip-line);
    padding: 22px; display: flex; flex-direction: column;
  }
  .sfip-alerts-head { flex: none; margin-bottom: 8px; }
  .sfip-alerts-title { font-size: 19px; font-weight: 500; color: var(--sfip-ink); }
  .sfip-alerts-empty {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; gap: 6px; padding: 20px;
  }
  .sfip-alerts-empty-icon {
    width: 52px; height: 52px; border-radius: 14px; background: #F5F2FA; color: #8C6FC0;
    display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
  }
  .sfip-alerts-empty-title { font-size: 15px; font-weight: 500; color: var(--sfip-ink); }
  .sfip-alerts-empty-text { font-size: 13px; color: #9A90AE; font-weight: 300; max-width: 220px; line-height: 1.5; }
`;